import { extendObservable, runInAction } from 'mobx';

import { ApiHttpService } from '../networking/ApiHttpService';
import { Logger } from '../helpers/Logger';
import { Storage } from '../helpers/Storage';
import { ParametersController } from '../networking/controllers/ParametersController';
import { constants } from '../config';
import { ProductsController } from '../networking/controllers/ProductsController';

const PARAMETERS_STORAGE_KEY = 'configstore-parameters';
const PLANS_STORAGE_KEY = 'configstore-plans';
const FEATUREFLAGS_STORAGE_KEY = 'configstore-featureflags';
const WEB_TOGGLE_STORAGE_KEY = 'configstore-intranet-toggle';
const MAX_RETRIES = 3;

class ConfigParametersStoreClass {
  constructor() {
    extendObservable(this, {
      parameters: null,
      plans: null,
      featureFlags: null,
      // This parameter only makes sense when showIntranet === true
      webToggled: false,
      get isIntranet() {
        return this.parameters && !!this.parameters.intraSearchFilters &&
          !!this.parameters.intraSearchFilters.length;
      },
      get showIntranet() {
        return !this.webToggled && this.isIntranet;
      },
    });
    this.retries = 0;
    this.fetchParametersFromStorage();
    this.fetchWebToggleFromStorage();
    this.fetchPlansFromStorage();
    this.fetchFeatureFlagsFromStorage();
  }

  toggleWeb() {
    runInAction(() => {
      this.webToggled = !this.webToggled;
    });
    Storage.set(WEB_TOGGLE_STORAGE_KEY, this.webToggled);
  }

  isFeatureFlagEnabled(featureFlagKey) {
    return this.featureFlags && this.featureFlags[featureFlagKey]
    && this.featureFlags[featureFlagKey].enabled;
  }

  isValueInFeatureFlagData(featureFlagKey, keyValue) {
    const data = this.featureFlags && this.featureFlags[featureFlagKey]
    && this.featureFlags[featureFlagKey].enabled
    && this.featureFlags[featureFlagKey].data[keyValue];
    if (data) {
      try {
        return JSON.parse(data);
      } catch (e) {
        return data;
      }
    }
    return null;
  }

  fetchWebToggleFromStorage() {
    const toggleValue = Storage.get(WEB_TOGGLE_STORAGE_KEY);
    runInAction(() => {
      this.webToggled = !!toggleValue;
    });
    if (this.webToggled && this.isIntranet) {
      // When the intranet user wants to see the web, we should send
      // the web parameter as true everywhere.
      ApiHttpService.setHeaders({
        web: 'true',
      });
    }
  }

  fetchParametersFromStorage() {
    const parameters = Storage.get(PARAMETERS_STORAGE_KEY);
    if (parameters) {
      Logger.log('Config Parameters found on storage');
      runInAction(() => {
        this.parameters = parameters;
      });
    }
    if (constants.refreshConfigParams) {
      Storage.remove(PARAMETERS_STORAGE_KEY);
    }
    this.fetchParameters();
  }

  fetchPlansFromStorage() {
    const plans = Storage.get(PLANS_STORAGE_KEY);
    if (plans) {
      Logger.log('Config Parameters found on storage');
      runInAction(() => {
        this.plans = plans;
      });
    }
    this.fetchPlans();
  }

  fetchFeatureFlagsFromStorage() {
    const featureFlags = Storage.get(FEATUREFLAGS_STORAGE_KEY);
    if (featureFlags) {
      Logger.log('Config Parameters found on storage');
      runInAction(() => {
        this.featureFlags = featureFlags;
      });
    }
    this.fetchFeatureFlags();
  }

  async fetchParameters() {
    if (this.retries > MAX_RETRIES) {
      Logger.warn('Parameter fetching exceeded retry limit.');
      return;
    }
    try {
      const parameters = await ParametersController.all();
      Storage.set(PARAMETERS_STORAGE_KEY, parameters);
      this.retries = 0;
      runInAction(() => {
        this.parameters = parameters;
      });
      Logger.log('Parameters loaded correctly');
    } catch (error) {
      Logger.warn(`Failed to fetch parameters, retried ${this.retries} times, error was: ${error.message}`);
      // Retry
      this.retries += 1;
      this.fetchParameters();
    }
  }

  async fetchPlans() {
    try {
      const plans = await ProductsController.findPlansNewApi();
      Storage.set(PLANS_STORAGE_KEY, plans);
      runInAction(() => {
        this.plans = plans;
      });
      Logger.log('Parameters loaded correctly');
    } catch (error) {
      Logger.warn(`Failed to fetch parameters, error was: ${error.message}`);
    }
  }

  async fetchFeatureFlags() {
    try {
      const featureFlags = await ParametersController.findFeatureFlagsNewApi();
      Storage.set(FEATUREFLAGS_STORAGE_KEY, featureFlags);
      runInAction(() => {
        this.featureFlags = featureFlags;
      });
    } catch (error) {
      Logger.warn(`Failed to fetch parameters, error was: ${error.message}`);
    }
  }
}

const ConfigParametersStore = new ConfigParametersStoreClass();

export { ConfigParametersStore };



// WEBPACK FOOTER //
// ./src/stores/ConfigParametersStore.js